import React, { useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { PlayArrow } from '@mui/icons-material';

import { useTestArnoTemplateVersion } from '../../hooks/reactQuerys/ArnoAdminQueries';
import { useGetLegalTransactions } from '../../hooks/data';
import { IPlanQuestion, PlanQuestions } from './PlanQuestions';
import { useAlert } from '../../context/AlertContext';

/**
 * Probelauf eines Entscheidungsbaums mit einem echten Vorgang (#1245).
 *
 * Dieselbe Runtime und derselbe Dokumentplan wie später bei der
 * Urkundenerzeugung, nur eben schon im Entwurf. Offene Fragen werden hier
 * beantwortet und der Lauf wiederholt — so sieht man im Editor, wie der Baum
 * reagiert, ohne ihn zu veröffentlichen.
 *
 * Es wird nichts nach Word geschrieben: das ist ein anderer Schritt.
 */

type IPendingQuestion = IPlanQuestion;

interface IPlanSection {
  order: number;
  label: string;
  status: string;
  previewText?: string;
}

interface IMissingValue {
  label: string | null;
  selector: string | null;
  section: string | null;
}

interface IPlan {
  sections: IPlanSection[];
  pendingQuestions: IPendingQuestion[];
  pendingCount: number;
  fingerprint: string;
  issues: { severity: string; code: string; message: string }[];
  unresolvedVariables: string[];
  /** Felder, die dieser Vorgang nicht füllen konnte (#1245). */
  missingValues?: IMissingValue[];
  model?: { source: string; versionNumber: number };
}

interface Props {
  open: boolean;
  onClose: () => void;
  idDecisionTemplateVersion: number;
  name: string;
  dirty: boolean;
}

export const TestRunDialog: React.FC<Props> = ({
  open,
  onClose,
  idDecisionTemplateVersion,
  name,
  dirty,
}) => {
  const { showAlert } = useAlert();
  const { legalTransactions, isLoadingLegalTransactions } =
    useGetLegalTransactions();
  const { testTemplateVersion, isTestingTemplateVersion } =
    useTestArnoTemplateVersion();

  const [transaction, setTransaction] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [plan, setPlan] = useState<IPlan | null>(null);

  const options = useMemo(
    () =>
      (legalTransactions ?? []).map(item => ({
        id: item.idLegalTransaction,
        label: `#${item.idLegalTransaction} ${item.Title ?? ''}`.trim(),
      })),
    [legalTransactions]
  );

  const run = async (nextAnswers: Record<string, string> = answers) => {
    try {
      const result = (await testTemplateVersion({
        idDecisionTemplateVersion,
        idLegalTransaction: transaction,
        answers: nextAnswers,
      })) as IPlan;
      setPlan(result);
    } catch (error) {
      showAlert({
        severity: 'error',
        text:
          error instanceof Error
            ? `Probelauf fehlgeschlagen: ${error.message}`
            : 'Probelauf fehlgeschlagen.',
      });
    }
  };

  const answer = (key: string, value: string) => {
    const next = { ...answers, [key]: value };
    setAnswers(next);
    void run(next);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle>
        Probelauf — {name}
        <Typography variant='caption' display='block' color='text.secondary'>
          Dieselbe Runtime wie bei der Urkundenerzeugung, auch für einen Entwurf
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {dirty && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            Es gibt ungespeicherte Änderungen. Der Probelauf benutzt den zuletzt
            gespeicherten Stand.
          </Alert>
        )}

        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
        >
          <Autocomplete
            options={options}
            loading={isLoadingLegalTransactions}
            sx={{ minWidth: 380 }}
            onChange={(_event, value) => setTransaction(value?.id ?? null)}
            renderInput={parameters => (
              <TextField
                {...parameters}
                size='small'
                label='Vorgang (optional)'
                helperText='Ohne Vorgang bleiben die DiNo-Felder leer.'
              />
            )}
          />
          <Button
            variant='contained'
            startIcon={
              isTestingTemplateVersion ? (
                <CircularProgress size={16} />
              ) : (
                <PlayArrow />
              )
            }
            onClick={() => run()}
            disabled={isTestingTemplateVersion}
          >
            Durchlaufen
          </Button>
          {plan && (
            <Chip
              size='small'
              variant='outlined'
              label={`Fingerabdruck ${plan.fingerprint.slice(0, 12)}`}
            />
          )}
        </Stack>

        {!plan ? (
          <Typography variant='body2' color='text.secondary'>
            Noch nicht gelaufen.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {plan.pendingQuestions.length > 0 && (
              <Box
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                }}
              >
                <Typography variant='subtitle2' sx={{ mb: 1 }}>
                  Offene Fragen ({plan.pendingCount})
                </Typography>
                <PlanQuestions
                  questions={plan.pendingQuestions}
                  answers={answers}
                  onAnswer={answer}
                />
              </Box>
            )}

            {(plan.missingValues ?? []).length > 0 && (
              <Alert severity='info'>
                <AlertTitle>
                  Fehlende Werte ({(plan.missingValues ?? []).length})
                </AlertTitle>
                {/*
                  Nicht der Baum ist kaputt — dieser Vorgang liefert die Werte
                  nicht. Das ist die Liste, die ein Sachbearbeiter braucht; im
                  Dokument stehen dieselben Stellen als sichtbare Fehlstelle.
                */}
                <Box component='ul' sx={{ m: 0, pl: 2 }}>
                  {(plan.missingValues ?? []).map((entry, index) => (
                    <li key={`${entry.selector}-${index}`}>
                      <Typography variant='body2' component='span'>
                        {entry.section ? `${entry.section}: ` : ''}
                        {entry.label ?? entry.selector}
                      </Typography>
                    </li>
                  ))}
                </Box>
              </Alert>
            )}

            <Box>
              <Typography variant='subtitle2' sx={{ mb: 1 }}>
                Ergebnis
              </Typography>
              {plan.sections.map(section => (
                <Box key={section.order} sx={{ mb: 1.5 }}>
                  <Stack direction='row' spacing={1} alignItems='center'>
                    <Chip
                      size='small'
                      color={section.status === 'ok' ? 'success' : 'default'}
                      label={
                        section.status === 'ok' ? 'im Dokument' : section.status
                      }
                    />
                    <Typography variant='body2' fontWeight={600}>
                      {section.order}. {section.label}
                    </Typography>
                  </Stack>
                  {section.previewText && (
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{
                        whiteSpace: 'pre-wrap',
                        pl: 1,
                        borderLeft: 2,
                        borderColor: 'divider',
                        mt: 0.5,
                      }}
                    >
                      {section.previewText}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            {plan.issues.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Typography variant='subtitle2'>
                    Hinweise aus dem Lauf
                  </Typography>
                  <Box component='ul' sx={{ m: 0, pl: 2 }}>
                    {plan.issues.slice(0, 10).map((issue, index) => (
                      <li key={`${issue.code}-${index}`}>
                        <Typography variant='body2'>{issue.message}</Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              </>
            )}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};
